import { useEffect } from 'react';

// 讓訊息區維持釘在最新訊息，直到使用者**主動**往上翻。
//
// ─────────────────────────────────────────────────────────────────────────────
// 這個 hook 修了五次才對。四個坑都記在這裡，避免之後有人重走
// ─────────────────────────────────────────────────────────────────────────────
//
// 【坑一：只觀察容器本身】容器的**盒子尺寸不會**因為內容變多而改變（變的是 scrollHeight），
// 所以「訊息載入／圖片載入撐開內容」這類變化，ResizeObserver(容器) 一次都收不到。
// → 必須連子節點一起觀察，並用 MutationObserver 追蹤子節點增減。
//
// 【坑二：用「現在在不在底部」判斷】圖片撐開內容時，瀏覽器會因捲動錨定**先發一次 scroll**，
// 此時用**新的** scrollHeight 重算必然得到「離底很遠」→ 旗標被設成 false → 後續釘底全被跳過，
// 畫面就停在圖片那一段。
// → 改為追蹤**使用者意圖**：預設黏著，只有 scrollTop **減少**（主動往上滑）才放開。
//   內容長高只會拉大與底部的距離、不會讓 scrollTop 減少，因此不再誤判。
//
// 【坑三：元素還沒掛上就跑 effect】對話室在旗標查完前會 `return <Center><Spinner/></Center>`，
// **帶 ref 的容器那時還沒渲染**。effect 首次執行時 scrollerRef.current 是 null，
// 而相依只有 [scrollerRef]、ref 物件識別永遠不變 → effect 再也不會重跑，整個 session 沒掛上。
// → 改成拿不到元素就等下一影格再試。
//   ⚠ 合成測試驗不出這一坑：測試都是在「已經存在的元素」上跑的。
//
// 【坑四：改到沒人 import 的檔案】曾經一度存在兩個同名檔案，坑二、坑三的修正都寫進了
// src/mobile/hooks/useStickToBottom.js，但兩個對話室 import 的其實是 **src/hooks/** 這一份。
// 於是連續兩輪「修好了但實機完全沒變化」。**這個檔案的正確位置是 src/hooks/**；
// 若日後又看到 src/mobile/hooks/useStickToBottom.js，那是孤兒檔，請刪掉。
//
// 另外：單次事件釘一次也不夠。「圖片載入→版面重算→事件送達」的先後在各瀏覽器不一致，
// 常常釘完之後內容才真正長高。所以任何成長訊號都開一段視窗，期間逐影格重釘。
const NEAR_BOTTOM_PX = 60;
const UP_TOLERANCE_PX = 4;    // 小於此值視為抖動，不算使用者往上滑
const SETTLE_MS = 600;        // 成長訊號之後持續重釘的時間

function setup(el) {
    let stick = true;                 // 使用者意圖：要不要黏在最新訊息
    let lastTop = el.scrollTop;

    const onScroll = () => {
        const top = el.scrollTop;
        if (top < lastTop - UP_TOLERANCE_PX) stick = false;                          // 主動往上滑 → 放開
        if (el.scrollHeight - top - el.clientHeight < NEAR_BOTTOM_PX) stick = true;  // 回到底部 → 重新黏上
        lastTop = top;
    };
    el.addEventListener('scroll', onScroll, { passive: true });

    // 只設 scrollTop、不改任何尺寸，所以不會造成 ResizeObserver 迴圈
    const pin = () => {
        if (!stick) return;
        el.scrollTop = el.scrollHeight;
        lastTop = el.scrollTop;   // 自己造成的位移不該被下一次 onScroll 誤判成「往上滑」
    };

    let watchUntil = 0;
    let rafId = 0;
    const tick = () => {
        if (!stick || performance.now() > watchUntil) { rafId = 0; return; }
        el.scrollTop = el.scrollHeight;
        lastTop = el.scrollTop;
        rafId = requestAnimationFrame(tick);
    };
    const settle = () => {
        watchUntil = performance.now() + SETTLE_MS;
        if (!rafId) rafId = requestAnimationFrame(tick);
    };

    const ro = new ResizeObserver(() => { pin(); settle(); });
    ro.observe(el);                                                                  // 可視高度
    const observeChildren = () => { for (const c of el.children) ro.observe(c); };   // 內容高度
    observeChildren();

    const mo = new MutationObserver(() => { observeChildren(); pin(); settle(); });
    mo.observe(el, { childList: true, subtree: false });

    // img 的 load 不會冒泡，但會走捕獲階段，所以用 capture 收
    const onLoad = (e) => { if (e.target && e.target.tagName === 'IMG') { pin(); settle(); } };
    el.addEventListener('load', onLoad, true);

    settle();   // 掛載當下也開一次（進入對話室時圖片正在陸續載入）

    return () => {
        cancelAnimationFrame(rafId);
        ro.disconnect();
        mo.disconnect();
        el.removeEventListener('scroll', onScroll);
        el.removeEventListener('load', onLoad, true);
    };
}

export default function useStickToBottom(scrollerRef) {
    useEffect(() => {
        if (typeof ResizeObserver === 'undefined') return undefined;

        let cancelled = false;
        let teardown = null;
        const attach = () => {
            if (cancelled) return;
            const el = scrollerRef.current;
            if (!el) { requestAnimationFrame(attach); return; }   // 見坑三：等元素出現
            teardown = setup(el);
        };
        attach();

        return () => { cancelled = true; if (teardown) teardown(); };
    }, [scrollerRef]);
}
